/*
 * Copyright (c) 2021 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { useState } from 'react';
import { UiType } from '../../../data/api/auth/authClient.typings';
import { DetailedUserDto, DetailedUsersListDto, UserStatus } from '../../../data/api/users/usersClient.typings';
import { UserTableData } from './AdminUsersListConfig';

export const isTaxInspectorRights = (accessRights: UiType[]): boolean => {
    return (
        accessRights.includes(UiType.Inspector) ||
        accessRights.includes(UiType.CentralInspector) ||
        accessRights.includes(UiType.RegionInspector)
    );
};

export const isOperatorRights = (accessRights: UiType[]): boolean => {
    return accessRights.includes(UiType.Operator);
};

export const findAndUpdateUserInfo = (
    updatedUser: DetailedUserDto,
    usersList: DetailedUsersListDto,
): DetailedUsersListDto => {
    const newUsersList = { ...usersList };
    for (let i = 0; i < newUsersList.users.length; i++) {
        if (newUsersList.users[i].id === updatedUser.id) {
            newUsersList.users.splice(i, 1, updatedUser);
            break;
        }
    }

    return newUsersList;
};

export const saveAddedUser = (addedUser: DetailedUserDto, addedUsersList: DetailedUserDto[]): DetailedUserDto[] => {
    const newAddedUsersList = [...addedUsersList];
    newAddedUsersList.push(addedUser);
    return newAddedUsersList;
};

export const areDeletedUsersShown = (tableData: UserTableData[]): boolean => {
    for (let i = 0; i < tableData.length; i++) {
        if (tableData[i].status === UserStatus.Deleted) return true;
    }
    return false;
};

export const useInput = (validateValue: (value: string) => boolean) => {
    const [enteredValue, setEnteredValue] = useState('');
    const [isTouched, setIsTouched] = useState(false);

    const valueIsValid = validateValue ? validateValue(enteredValue) : true;
    const hasError = !valueIsValid && isTouched;

    const valueChangeHandler = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value?: string) => {
        setIsTouched(true);
        if (value) setEnteredValue(value);
        else setEnteredValue(event.target.value);
    };

    const inputBlurHandler = (_event: React.FocusEvent<HTMLInputElement>) => {
        setIsTouched(true);
    };

    const reset = () => {
        setEnteredValue('');
        setIsTouched(false);
    };

    const setValue = (value: string) => {
        if (value) setEnteredValue(value);
        else setEnteredValue('');
    };

    return {
        value: enteredValue,
        isValid: valueIsValid,
        hasError,
        valueChangeHandler,
        inputBlurHandler,
        reset,
        setValue,
    };
};

export const useMultipleSelectInput = (validateValue: (checkValues: string[]) => boolean) => {
    const [selectedValues, setSelectedValues] = useState([]);
    const [isTouched, setIsTouched] = useState(false);

    const valueIsValid = validateValue ? validateValue(selectedValues) : true;
    const hasError = !valueIsValid && isTouched;

    const valuesChangeHandler = (values: string[]) => {
        setSelectedValues(values);
    };

    const inputBlurHandler = () => {
        setIsTouched(true);
    };

    const reset = () => {
        setSelectedValues([]);
        setIsTouched(false);
    };

    const setValues = (values: string[]) => {
        if (values) setSelectedValues(values);
        else setSelectedValues([]);
    };

    return {
        selectedValues,
        isValid: valueIsValid,
        hasError,
        valuesChangeHandler,
        inputBlurHandler,
        reset,
        setValues,
    };
};
